import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface PortalUserRow {
  readonly portalUserId: string;
  readonly userCode: string;
  readonly displayName: string;
  readonly email: string;
  readonly userType: string;
  readonly status: string;
}

const USER_TYPES = ['CUSTOMER', 'VENDOR', 'AGENCY', 'DEALER', 'DISTRIBUTOR', 'PARTNER', 'CONTRACTOR', 'CONSULTANT', 'AUDITOR', 'EXTERNAL_APPROVER'];
const STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED', 'LOCKED', 'DEACTIVATED'];
const PAGE = 25;

/**
 * M42 §27 AdministrationConsole (§7 / §14). Portal administrators register
 * external stakeholder accounts, review the roster and deactivate accounts.
 * Viewing needs M42_USER_VIEW, registering needs M42_USER_REGISTER,
 * deactivating needs M42_USER_MANAGE.
 */
export function AdministrationConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PortalUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('CUSTOMER');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: PortalUserRow[]; total: number }>(`/v1/portal/users?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, page, reload]);

  const register = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!displayName.trim()) { setActionError(t('m42.admin.displayNameRequired')); return; }
    if (!email.trim()) { setActionError(t('m42.admin.emailRequired')); return; }
    try {
      await apiRequest('/v1/portal/users/register', {
        method: 'POST',
        body: { displayName: displayName.trim(), email: email.trim(), userType },
        token,
      });
      setNotice(t('m42.admin.registered'));
      setDisplayName('');
      setEmail('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.admin.notAuthRegister'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const deactivate = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/portal/users/${id}/deactivate`, { method: 'POST', body: {}, token });
      setNotice(t('m42.admin.deactivated'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.admin.notAuthDeactivate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="administration-console">
      <h1>{t('m42.admin.title')}</h1>

      <section aria-label="user-register">
        <h2>{t('m42.admin.registerHeading')}</h2>
        <form aria-label="register-form" onSubmit={(e) => void register(e)}>
          <label>{t('m42.admin.displayNameLabel')}<input aria-label="field-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
          <label>{t('m42.admin.email')}<input aria-label="field-email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label>{t('m42.common.type')}
            <select aria-label="field-user-type" value={userType} onChange={(e) => setUserType(e.target.value)}>
              {USER_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-register">{t('m42.admin.registerBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="user-action-error">{actionError}</p>}
        {notice && <p aria-label="user-notice">{notice}</p>}
      </section>

      <section aria-label="user-filters">
        <label>{t('m42.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m42.common.all')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="user-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="user-total">{t('m42.admin.total', { count: total })}</p>
          <DataTable
            ariaLabel="user-table"
            rows={rows}
            rowKey={(u) => u.portalUserId}
            emptyMessage={t('m42.admin.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (u) => `user-code-${u.portalUserId}`, render: (u) => u.userCode },
              { key: 'name', header: t('m42.admin.nameCol'), render: (u) => u.displayName },
              { key: 'email', header: t('m42.admin.email'), render: (u) => u.email },
              { key: 'type', header: t('m42.common.type'), render: (u) => u.userType },
              { key: 'status', header: t('m42.common.status'), cellAriaLabel: (u) => `user-status-${u.portalUserId}`, render: (u) => u.status },
              { key: 'actions', header: t('m42.common.actions'), render: (u) => (
                <Button variant="danger" type="button" aria-label={`deactivate-${u.portalUserId}`} onClick={() => void deactivate(u.portalUserId)}>{t('m42.admin.deactivateBtn')}</Button>
              ) },
            ]}
          />

          <nav aria-label="user-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
